// import { Controller } from "react-hook-form";
import { icons } from "lucide-react";
import React from "react";
import { Controller } from "react-hook-form";
import { Button, Icon, SelectComponents } from "~/components/ui";
import { InputTypes, Option } from "~/lib/constants";
import { Attachment } from "~/lib/types";
import { cn } from "~/lib/utils";

import {
  ErrorProps,
  FormDateInput,
  FormDateTimeInput,
  FormDomainInput,
  FormEmailInput,
  FormExternalInput,
  FormFileInput,
  FormFilesUploadInput,
  FormInternalInput,
  FormMomentInput,
  FormNumberInput, // FormNumberInput,
  FormPasswordInput,
  FormRadioInput,
  FormRichTextInput,
  FormSelectInput,
  FormSignatureInput,
  FormTextInput,
  FormTextareaInput,
  FormTimeInput,
} from ".";
import { AddUserProps } from "../shared/user-lists/add-user";
import { SuggestionProps } from "../ui/adusei-editor/suggestion";
import FormCheckInput from "./form-check";
import FormDate2Input from "./form-date-2";
import FormFilesDropzoneInput from "./form-dropzone";
import FormMultiSelectInput from "./form-multi-select";
import FormSearchableSelectInput from "./form-searchable-select";

interface SpaceProps {
  type: InputTypes.SPACE;
}
interface BaseInputProps {
  errors: ErrorProps;
  autoFocus?: boolean;
  readOnly?: boolean;
  required?: boolean;
  label: string;
  className?: string;
  placeholder?: string;
  id?: string;
  autoComplete?: string;
  // name?: string;
}
type TextInputProps = BaseInputProps & {
  type:
    | InputTypes.TEXT
    | InputTypes.TEXTAREA
    | InputTypes.EMAIL
    | InputTypes.PASSWORD
    | InputTypes.DOMAIN
    | InputTypes.NUMBER;
  register: any;
  value?: string;
  rows?: number;
  suffix?: keyof typeof icons;
  prefix?: keyof typeof icons;
  suffixClass?: string;
  prefixText?: string;
  suffixText?: string;
  description?: React.ReactNode;
  step?: string;
};

type TimeInputProps = BaseInputProps & {
  type: InputTypes.TIME;
  control: any;
  name: string;
  defaultValue?: string;
};

type UserInputProps = BaseInputProps & {
  type: InputTypes.USER_SELECT;
  control: any;
  name: string;
  title?: string;
  value?: Option[] | AddUserProps[];
  options?: Option[];
  external?: boolean;
};
type PhoneInputProps = BaseInputProps & {
  type: InputTypes.PHONE | InputTypes.OTP | InputTypes.MOMENT;
  control: any;
  name: string;
  defaultValue?: string;
  moment?: boolean;
};
type SignInputProps = BaseInputProps & {
  type: InputTypes.SIGNATURE;
  control: any;
  name: string;
};
type FileInputProps = BaseInputProps & {
  type: InputTypes.IMAGE | InputTypes.COLOR;
  control: any;
  name: string;
  defaultValue?: string;
};
type FilesUploadInputProps = BaseInputProps & {
  type: InputTypes.UPLOAD;
  control: any;
  name: string;
  defaultValue: FileList | null; // Updated to support URL arrays
  multiple?: boolean;
};
type FilesDragDropInputProps = BaseInputProps & {
  type: InputTypes.DRAGNDROP;
  control: any;
  name: string;
  defaultValue: Attachment[] | FileList | null;
  multiple?: boolean;
  onFilesChange?: (files: FileList | null) => void; // Only files
  onRemovedFiles?: (removedFiles: Attachment[]) => void; // Separate callback for removed files
};

type CheckboxInputProps = BaseInputProps & {
  type: InputTypes.CHECKBOX;
  control: any;
  name: string;
  defaultValue?: boolean;
  value?: boolean;
  options: Option[];
};
type DateInputProps = BaseInputProps & {
  type: InputTypes.DATE | InputTypes.DATETIME;
  kind?: "default" | "extensive";
  control: any;
  name: string;
  maxDetail?: "decade" | "year" | "month" | "monthYear";
  disabled?: {
    after: Date;
  };
};
type RichTextInputProps = BaseInputProps & {
  type: InputTypes.RICHTEXT;
  control: any;
  name: string;
  suggestions?: SuggestionProps[];
};
type SelectInputProps = BaseInputProps & {
  type: InputTypes.SELECT;
  control?: any;
  name: string;
  options: Option[];
  defaultValue?: Option;
  value?: Option;
  components?: Partial<SelectComponents>;
  isLoading?: boolean;
  onChange?: (...event: any[]) => void;
  isDisabled?: boolean;
  onModal?: boolean;
};
type SearchableSelectInputProps = BaseInputProps & {
  type: InputTypes.SEARCHABLE_SELECT;
  control?: any;
  name: string;
  options: Option[];
  defaultValue?: Option;
  value?: Option;
  searchPlaceholder?: string;
  prefix?: React.ReactNode;
  onChange?: (...event: any[]) => void;
};
type MultiInputProps = BaseInputProps & {
  type: InputTypes.MULTI;
  control?: any;
  name: string;
  options: Option[];
  defaultValue?: Option[];
  values?: Option[];
  emptyStateText?: string;
};
type RadioInputProps = BaseInputProps & {
  control?: any;
  name: string;
  options: Option[];
  type: InputTypes.RADIO;
};
interface ButtonProps {
  type: InputTypes.SUBMIT;
  title: string;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  size?: "default" | "sm" | "lg" | "icon" | null | undefined;
  prefix?: keyof typeof icons;
  prefixClass?: string;
  suffix?: keyof typeof icons;
  suffixClass?: string;
  variant?: "default" | "outline" | "ghost" | "secondary" | "destructive";
}
export type FormInput =
  | TextInputProps
  | SelectInputProps
  | SearchableSelectInputProps
  | MultiInputProps
  | PhoneInputProps
  | FileInputProps
  | UserInputProps
  | CheckboxInputProps
  | FilesUploadInputProps
  | DateInputProps
  | RadioInputProps
  | RichTextInputProps
  | FilesDragDropInputProps
  | ButtonProps
  | SignInputProps
  | SpaceProps
  | TimeInputProps
  | null
  | undefined;

interface Props {
  className?: string;
  config: FormInput[];
  fieldWrapperClassName?: string;
  wizardWrapperClassName?: string;
  suffix?: React.FC;
}

export default function FormWizard({
  className,
  fieldWrapperClassName,
  config,
  wizardWrapperClassName,
  ...props
}: Props) {
  // console.log(config, "config");
  return (
    <div className={cn("w-full", wizardWrapperClassName)}>
      <div className={cn("space-y-4", className)}>
        {config?.map(
          (formInput: FormInput, index) =>
            formInput && (
              <div key={index} className={cn(fieldWrapperClassName)}>
                {FormWizardSwitch(formInput)}
              </div>
            ),
        )}
      </div>
      {props.suffix && <props.suffix />}
    </div>
  );
}

const FormWizardSwitch = (formInput: FormInput) => {
  if (formInput === null || formInput === undefined) return null;

  switch (formInput.type) {
    case InputTypes.DOMAIN:
      return <FormDomainInput {...formInput} />;
    case InputTypes.TEXT:
      return <FormTextInput {...formInput} />;

    case InputTypes.NUMBER:
      return <FormNumberInput {...formInput} />;
    case InputTypes.TEXTAREA:
      return <FormTextareaInput {...formInput} />;
    case InputTypes.EMAIL:
      return <FormEmailInput {...formInput} />;
    case InputTypes.PASSWORD:
      return <FormPasswordInput {...formInput} />;
    case InputTypes.SELECT:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => (
            <FormSelectInput
              value={value}
              onModal={formInput.onModal}
              isMulti={false}
              defaultValue={value || formInput.defaultValue || value}
              label={formInput.label}
              required={formInput.required}
              onChange={formInput.onChange || onChange}
              errors={formInput.errors}
              options={formInput.options}
              autoFocus={formInput.autoFocus}
              placeholder={formInput.placeholder}
              components={formInput.components}
              isLoading={formInput.isLoading}
              isDisabled={formInput.isDisabled}
            />
          )}
        />
      );
    case InputTypes.CHECKBOX:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => (
            <FormCheckInput
              label={formInput.label}
              required={formInput.required}
              onChange={onChange}
              value={value || []}
              errors={formInput.errors}
              options={formInput.options}
            />
          )}
        />
      );
    case InputTypes.RADIO:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field }) => (
            <FormRadioInput
              required={formInput.required}
              label={formInput.label}
              options={formInput.options}
              defaultValue={field.value}
              onChange={(opt: string) => {
                field.onChange(opt);
              }}
              errors={formInput.errors}
            />
          )}
        />
      );

    case InputTypes.USER_SELECT:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => (
            <>
              {formInput?.external ? (
                <FormExternalInput
                  label={formInput.label}
                  required={formInput.required}
                  errors={formInput.errors}
                  value={value}
                  onChange={onChange}
                  className={formInput.className}
                  title={formInput.title}
                />
              ) : (
                <FormInternalInput
                  title={formInput.title}
                  options={formInput.options}
                  label={formInput.label}
                  required={formInput.required}
                  errors={formInput.errors}
                  value={value}
                  onChange={onChange}
                  className={formInput.className}
                />
              )}
            </>
          )}
        />
      );
    case InputTypes.MOMENT:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => (
            <>
              {formInput?.moment ? (
                <FormMomentInput
                  defaultValue={formInput.defaultValue}
                  label={formInput.label}
                  required={formInput.required}
                  errors={formInput.errors}
                  value={value}
                  onChange={onChange}
                />
              ) : (
                <FormTimeInput
                  defaultValue={formInput.defaultValue}
                  label={formInput.label}
                  required={formInput.required}
                  errors={formInput.errors}
                  value={value}
                  onChange={onChange}
                />
              )}
            </>
          )}
        />
      );
    case InputTypes.SIGNATURE:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange } }) => (
            <FormSignatureInput
              label={formInput.label}
              required={formInput.required}
              errors={formInput.errors}
              onChange={onChange}
            />
          )}
        />
      );
    case InputTypes.SEARCHABLE_SELECT:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => {
            return (
              <FormSearchableSelectInput
                value={value}
                defaultValue={value || formInput.defaultValue || value}
                label={formInput.label}
                required={formInput.required}
                readOnly={formInput.readOnly}
                onChange={formInput.onChange || onChange}
                errors={formInput.errors}
                options={formInput.options}
                autoFocus={formInput.autoFocus}
                placeholder={formInput.placeholder}
                searchPlaceholder={formInput.searchPlaceholder}
                prefix={formInput.prefix}
              />
            );
          }}
        />
      );

    case InputTypes.MULTI:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => (
            <FormMultiSelectInput
              defaultValue={formInput.defaultValue || value}
              label={formInput.label}
              required={formInput.required}
              onChange={onChange}
              errors={formInput.errors}
              options={formInput.options}
              autoFocus={formInput.autoFocus}
              placeholder={formInput.placeholder}
              emptyStateText={formInput.emptyStateText}
            />
          )}
        />
      );

    case InputTypes.IMAGE:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange } }) => (
            <FormFileInput
              defaultValue={formInput.defaultValue}
              label={formInput.label}
              required={formInput.required}
              onChange={onChange}
              errors={formInput.errors}
            />
          )}
        />
      );

    case InputTypes.UPLOAD:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange } }) => (
            <FormFilesUploadInput
              name={formInput.name}
              multiple={formInput.multiple}
              defaultValue={formInput.defaultValue}
              label={formInput.label}
              required={formInput.required}
              onChange={onChange}
              errors={formInput.errors}
            />
          )}
        />
      );
    // case InputTypes.DRAGNDROP:
    //   return (
    //     <Controller
    //       control={formInput.control}
    //       name={formInput.name}
    //       render={({ field: { onChange, value } }) => (
    //         <FormFilesDropzoneInput
    //           name={formInput.name}
    //           defaultValue={formInput.defaultValue}
    //           value={value}
    //           label={formInput.label}
    //           required={formInput.required}
    //           onChange={onChange}
    //           errors={formInput.errors}
    //         />
    //       )}
    //     />
    //   );
    case InputTypes.DRAGNDROP:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => {
            const handleFilesChange = (files: FileList | null) => {
              // Update form state with files
              onChange(files);

              // Call custom callback if provided
              if (formInput.onFilesChange) {
                formInput.onFilesChange(files);
              }
            };

            return (
              <FormFilesDropzoneInput
                name={formInput.name}
                defaultValue={formInput.defaultValue}
                value={value}
                label={formInput.label}
                required={formInput.required}
                onChange={handleFilesChange}
                onRemovedFiles={formInput.onRemovedFiles}
                errors={formInput.errors}
              />
            );
          }}
        />
      );
    case InputTypes.RICHTEXT:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => (
            <FormRichTextInput
              label={formInput.label}
              required={formInput.required}
              autoFocus={formInput.autoFocus}
              readOnly={formInput.readOnly}
              onChange={onChange}
              value={value}
              suggestions={formInput.suggestions}
              errors={formInput.errors}
              className={formInput.className}
            />
          )}
        />
      );
    case InputTypes.DATE:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => (
            <>
              {formInput.kind === "extensive" ? (
                <FormDate2Input
                  label={formInput.label}
                  required={formInput.required}
                  onChange={onChange}
                  value={value}
                  disabled={formInput.disabled}
                  className={formInput.className}
                  errors={formInput.errors}
                  maxDetail={formInput.maxDetail as any}
                />
              ) : (
                <FormDateInput
                  label={formInput.label}
                  required={formInput.required}
                  onChange={onChange}
                  value={value}
                  disabled={formInput?.disabled}
                  className={formInput.className}
                  errors={formInput.errors}
                />
              )}
            </>
          )}
        />
      );

    case InputTypes.DATETIME:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => (
            <FormDateTimeInput
              label={formInput.label}
              required={formInput.required}
              onChange={onChange}
              value={value}
              errors={formInput.errors}
            />
          )}
        />
      );

    case InputTypes.SUBMIT:
      return (
        <Button
          disabled={formInput.disabled}
          onClick={formInput.onClick}
          type="submit"
          size={formInput.size}
          className={formInput.className}
          variant={formInput.variant ? formInput.variant : "secondary"}
        >
          {formInput.loading ? (
            <Icon name="LoaderCircle" className="mr-2 animate-spin" />
          ) : (
            formInput.prefix && (
              <Icon
                className={cn("pr-2", formInput.prefixClass)}
                name={formInput.prefix}
              />
            )
          )}
          {formInput.title}
        </Button>
      );
    case InputTypes.TIME:
      return (
        <Controller
          control={formInput.control}
          name={formInput.name}
          render={({ field: { onChange, value } }) => (
            <FormTimeInput
              defaultValue={formInput.defaultValue}
              label={formInput.label}
              required={formInput.required}
              errors={formInput.errors}
              value={value}
              onChange={onChange}
            />
          )}
        />
      );
    case InputTypes.SPACE:
      return <div></div>;
    default:
      return <div>Nothing</div>;
  }
};
